import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  F0AvatarList,
  F0TagDot,
  F0TagRaw,
  F0TagTeam,
  ProgressBarChart,
} from '@factorialco/f0-react'
import {
  SideContentSurveyFragment,
  SurveyEmployeeParticipantsDocument,
  SurveyLegalEntityParticipantsDocument,
  SurveyLocationParticipantsDocument,
  SurveyTeamParticipantsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

const MAX_TAG_ITEMS = 10

interface Props {
  survey: SideContentSurveyFragment
}

export function SideContent({ survey }: Props) {
  const entitiesIds = (survey?.respondentStrategy.arguments || []).map(String)
  const respondentStrategy = survey?.respondentStrategy.strategy

  const { data: teamsData } = useQuery(SurveyTeamParticipantsDocument, {
    variables: { teamIds: entitiesIds, maxItems: MAX_TAG_ITEMS },
    skip: entitiesIds.length === 0 || respondentStrategy !== 'teams',
    fetchPolicy: 'cache-first',
  })
  const teams = (teamsData?.teams?.teamsConnection?.edges || []).map((edge) => edge.node)
  const totalTeams = teamsData?.teams?.teamsConnection?.totalCount || 0

  const { data: locationsData } = useQuery(SurveyLocationParticipantsDocument, {
    variables: { locationIds: entitiesIds, maxItems: MAX_TAG_ITEMS },
    skip: entitiesIds.length === 0 || respondentStrategy !== 'locations',
    fetchPolicy: 'cache-first',
  })
  const locations = locationsData?.locations?.locationsConnection?.nodes || []
  const totalLocations = locationsData?.locations?.locationsConnection?.totalCount || 0

  const { data: legalEntitiesData } = useQuery(SurveyLegalEntityParticipantsDocument, {
    variables: {
      legalEntityIds: entitiesIds,
      maxItems: MAX_TAG_ITEMS,
    },
    skip: entitiesIds.length === 0 || respondentStrategy !== 'legal_entities',
    fetchPolicy: 'cache-first',
  })
  const legalEntities = legalEntitiesData?.companies?.legalEntitiesConnection?.nodes || []
  const totalLegalEntities = legalEntitiesData?.companies?.legalEntitiesConnection?.totalCount || 0

  const { data: employeesData } = useQuery(SurveyEmployeeParticipantsDocument, {
    variables: { employeeIds: entitiesIds, maxItems: MAX_TAG_ITEMS },
    skip: entitiesIds.length === 0 || respondentStrategy !== 'employees',
    fetchPolicy: 'cache-first',
  })
  const employees = employeesData?.employees?.employeeNamesConnection?.nodes || []
  const totalEmployees = employeesData?.employees?.employeeNamesConnection?.totalCount || 0

  const sentCount = Number(survey.formWithProgress?.sentCount || 0)
  const answeredCount = Number(survey.formWithProgress?.answeredCount || 0)
  const participationPercentage =
    sentCount > 0 ? Math.max(0, Math.min(100, Math.round((answeredCount / sentCount) * 100))) : 0
  const isActiveOrFinished = survey.form.status === 'active' || survey.form.status === 'finished'

  return (
    <Box paddingLeft="s16" gap="s16" maxWidth="s300">
      <Text.Normal size="100" weight="medium" caps color="grey800">
        {i18n.t('surveys.configuration.title')}
      </Text.Normal>

      <Box gap="s8" overflowX="hidden">
        <Text.Normal size="200" color="grey800">
          {i18n.t('surveys.configuration.editors.title')}
        </Text.Normal>

        <F0AvatarList
          type="person"
          max={MAX_TAG_ITEMS}
          size="sm"
          layout="fill"
          avatars={survey.editors
            .filter(
              (
                editor
              ): editor is typeof editor & { employee: NonNullable<typeof editor.employee> } =>
                editor.employee != null
            )
            .map((editor) => ({
              firstName: editor.employee.fullName,
              lastName: '',
              src: buildUrl(editor.employee.avatar?.url),
            }))}
        />
      </Box>

      <Box gap="s8">
        <Text.Normal size="200" color="grey800">
          {i18n.t('surveys.configuration.privacy_settings.title')}
        </Text.Normal>

        {survey.form.anonymous || survey.restrictedViewersEnabled ? (
          <Box alignItems="flexStart" gap="s8">
            {survey.form.anonymous && (
              <F0TagDot
                text={i18n.t('surveys.configuration.privacy_settings.anonymous_answers')}
                color="yellow"
              />
            )}

            {survey.restrictedViewersEnabled && (
              <F0TagDot
                text={i18n.t('surveys.configuration.visibility.restricted_viewers.title')}
                color="yellow"
              />
            )}
          </Box>
        ) : (
          <Text.Normal size="100" color="grey800">
            {i18n.t('surveys.configuration.privacy_settings.not_configured')}
          </Text.Normal>
        )}
      </Box>

      <Box gap="s8">
        <Text.Normal size="200" color="grey800">
          {i18n.t('surveys.configuration.participants.title')}
        </Text.Normal>

        {survey.respondentStrategy.strategy === 'everyone' && (
          <Box flexDirection="row">
            <F0TagTeam name={i18n.t('surveys.configuration.participants.options.everyone.label')} />
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'all_employees' && (
          <Box flexDirection="row">
            <F0TagTeam
              name={i18n.t('surveys.configuration.participants.options.all_employees.label')}
            />
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'managers' && (
          <Box flexDirection="row">
            <F0TagTeam name={i18n.t('surveys.configuration.participants.options.managers.label')} />
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'employees' && (
          <Box gap="s8">
            <F0AvatarList
              type="person"
              max={MAX_TAG_ITEMS}
              size="sm"
              layout="fill"
              avatars={employees.map((employee) => ({
                firstName: employee.fullName,
                lastName: '',
                src: buildUrl(employee.avatar?.url),
              }))}
              remainingCount={
                totalEmployees > MAX_TAG_ITEMS ? totalEmployees - MAX_TAG_ITEMS : undefined
              }
            />

            {totalEmployees == 0 && (
              <Text.Normal size="100" color="grey800">
                {i18n.t('surveys.configuration.participants.not_configured')}
              </Text.Normal>
            )}
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'teams' && (
          <Box flexWrap="wrap" flexDirection="row" gap="s8">
            {teams.map((team) => (
              <F0TagTeam key={team.id} name={team.name} src={team.avatar || ''} />
            ))}

            {totalTeams > MAX_TAG_ITEMS && <F0TagRaw text={`+${totalTeams - MAX_TAG_ITEMS}`} />}

            {totalTeams == 0 && (
              <Text.Normal size="100" color="grey800">
                {i18n.t('surveys.configuration.participants.not_configured')}
              </Text.Normal>
            )}
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'locations' && (
          <Box flexWrap="wrap" flexDirection="row" gap="s8">
            {locations.map((location) => (
              <F0TagTeam key={location.id} name={location.name} />
            ))}

            {totalLocations > MAX_TAG_ITEMS && (
              <F0TagRaw text={`+${totalLocations - MAX_TAG_ITEMS}`} />
            )}

            {totalLocations == 0 && (
              <Text.Normal size="100" color="grey800">
                {i18n.t('surveys.configuration.participants.not_configured')}
              </Text.Normal>
            )}
          </Box>
        )}

        {survey.respondentStrategy.strategy === 'legal_entities' && (
          <Box flexWrap="wrap" flexDirection="row" gap="s8">
            {legalEntities.map((legalEntity) => (
              <F0TagTeam key={legalEntity.id} name={legalEntity.legalName} />
            ))}

            {totalLegalEntities > MAX_TAG_ITEMS && (
              <F0TagRaw text={`+${totalLegalEntities - MAX_TAG_ITEMS}`} />
            )}

            {totalLegalEntities == 0 && (
              <Text.Normal size="100" color="grey800">
                {i18n.t('surveys.configuration.participants.not_configured')}
              </Text.Normal>
            )}
          </Box>
        )}
      </Box>

      {isActiveOrFinished && (
        <Box gap="s8">
          <Text.Normal size="200" color="grey800">
            {i18n.t('surveys.participation_card.title')}
          </Text.Normal>

          <ProgressBarChart value={participationPercentage} max={100} color="feedback-positive" />

          <Text.Normal size="100" color="grey800">
            {answeredCount} / {sentCount} {i18n.t('surveys.participation_card.responses_label')}
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}
